import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Stack,
  Group,
  Text,
  Select,
  TextInput,
  Table,
  Pagination,
  Modal,
  Divider,
  useMantineColorScheme,
  LoadingOverlay,
  ActionIcon,
  Tooltip,
  Button,
} from '@mantine/core';
import {
  IconSearch,
  IconStar,
  IconStarFilled,
  IconEye,
  IconRefresh,
  IconMessageReport,
  IconExternalLink,
  IconTrash,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageTransition from '../../components/animations/PageTransition';
import AdminPageHeader from '../../components/AdminPageHeader';
import {
  quejaSugerenciaService,
  QuejaSugerenciaAdminDTO,
} from '../../services/quejaSugerenciaService';

const QuejasSugerenciasPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const [loading, setLoading] = useState(true);
  const [quejas, setQuejas] = useState<QuejaSugerenciaAdminDTO[]>([]);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState<string>('');
  const [ratingFilter, setRatingFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<QuejaSugerenciaAdminDTO | null>(null);
  // Reporte pendiente de confirmación de borrado
  const [aBorrar, setABorrar] = useState<QuejaSugerenciaAdminDTO | null>(null);
  const [borrando, setBorrando] = useState(false);
  const itemsPerPage = 15;

  const dark = colorScheme === 'dark';
  // Paleta sobria unificada con Reactivos/Dashboard.
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textPrimary = dark ? '#e2e8f0' : '#2d2a26';
  const textMuted = dark ? '#94a3b8' : '#8c8478';

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await quejaSugerenciaService.listarParaAdmin();
      setQuejas(data);
    } catch (e) {
      console.error('Error cargando quejas:', e);
    } finally {
      setLoading(false);
    }
  };

  const confirmarBorrado = async () => {
    if (!aBorrar) return;
    setBorrando(true);
    try {
      await quejaSugerenciaService.eliminar(aBorrar.id!);
      setABorrar(null);
      await fetchData();
      notifications.show({
        title: 'Reporte eliminado',
        message: 'El reporte se eliminó correctamente.',
        color: 'green',
      });
    } catch (e) {
      console.error('Error eliminando reporte:', e);
      notifications.show({
        title: 'Error',
        message: 'No se pudo eliminar el reporte. Inténtalo de nuevo.',
        color: 'red',
      });
    } finally {
      setBorrando(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    return quejas.filter((q) => {
      if (tipoFilter && (q.tipoObjetivo || '').toLowerCase() !== tipoFilter.toLowerCase()) return false;
      if (ratingFilter && String(q.rating) !== ratingFilter) return false;
      if (search.trim()) {
        const term = search.trim().toLowerCase();
        const hay = (s?: string) => (s || '').toLowerCase().includes(term);
        if (
          !hay(q.usuarioEmail) &&
          !hay(q.usuarioUsername) &&
          !hay(q.usuarioNombre) &&
          !hay(q.usuarioApellidos) &&
          !hay(q.comentario) &&
          !hay(q.preguntaTexto)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [quejas, tipoFilter, ratingFilter, search]);

  const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const formatDate = (s: string) =>
    new Date(s).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const renderStars = (rating: number) => (
    <Group gap={2} wrap="nowrap">
      {Array.from({ length: 5 }).map((_, i) =>
        i < rating ? (
          <IconStarFilled key={i} size={14} color="#f59e0b" />
        ) : (
          <IconStar key={i} size={14} color={textMuted} />
        )
      )}
    </Group>
  );

  const tipoBadge = (tipo: string) => {
    const t = (tipo || '').toLowerCase();
    const map: Record<string, { color: string; label: string }> = {
      reactivo: { color: dark ? '#8aa9d6' : '#5c7bb0', label: 'Reactivo' },
      pregunta_caso: { color: dark ? '#a99bd1' : '#9385c0', label: 'Pregunta caso' },
      flashcard: { color: dark ? '#86b8a6' : '#4f8a74', label: 'Flashcard' },
      video: { color: dark ? '#cdb487' : '#a07d3c', label: 'Video' },
    };
    const cfg = map[t] || { color: textMuted, label: tipo };
    return (
      <Text size="xs" fw={600} style={{ color: cfg.color }}>
        {cfg.label}
      </Text>
    );
  };

  const usuarioDisplay = (q: QuejaSugerenciaAdminDTO) => {
    if (q.usuarioNombre || q.usuarioApellidos) {
      return `${q.usuarioNombre ?? ''} ${q.usuarioApellidos ?? ''}`.trim();
    }
    return q.usuarioUsername || q.usuarioEmail || `Usuario #${q.usuarioId}`;
  };

  // Construye la URL para ir a la página admin correspondiente con el texto pre-cargado (?q=).
  const linkConfig = (q: QuejaSugerenciaAdminDTO): { url: string; label: string } | null => {
    const tipo = (q.tipoObjetivo || '').toLowerCase();
    if (!q.preguntaTexto) return null;

    // Usar los primeros 60 chars para que el .includes() del buscador matche bien.
    const queryText = q.preguntaTexto.slice(0, 60).trim();
    const q64 = encodeURIComponent(queryText);

    if (tipo === 'reactivo') {
      return { url: `/admin/question-database?q=${q64}`, label: 'Ver reactivo' };
    }
    if (tipo === 'pregunta_caso') {
      return { url: `/admin/clinical-cases?q=${q64}`, label: 'Ver pregunta caso' };
    }
    if (tipo === 'flashcard') {
      return { url: `/admin/flashcards?q=${q64}`, label: 'Ver flashcard' };
    }
    if (tipo === 'video') {
      return { url: `/admin/videos?q=${q64}`, label: 'Ver video' };
    }
    return null;
  };

  return (
    <PageTransition type="medical" duration={800}>
      <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto', position: 'relative' }}>
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ blur: 2 }} />

        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <AdminPageHeader
              title="Quejas y Sugerencias"
              subtitle={`${filtered.length} ${filtered.length === 1 ? 'reporte' : 'reportes'} de usuarios`}
            />
            <Tooltip label="Recargar">
              <ActionIcon variant="subtle" color="gray" size="lg" onClick={fetchData}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* Filtros */}
          <Card withBorder radius="lg" p="md" style={cardStyle}>
            <Group wrap="wrap" gap="md">
              <TextInput
                placeholder="Buscar por usuario, comentario o pregunta..."
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  setPage(1);
                }}
                leftSection={<IconSearch size={16} />}
                style={{ flex: 1, minWidth: 240 }}
              />
              <Select
                placeholder="Tipo"
                value={tipoFilter}
                onChange={(v) => {
                  setTipoFilter(v || '');
                  setPage(1);
                }}
                data={[
                  { value: '', label: 'Todos los tipos' },
                  { value: 'reactivo', label: 'Reactivos' },
                  { value: 'pregunta_caso', label: 'Preguntas de caso' },
                  { value: 'flashcard', label: 'Flashcards' },
                  { value: 'video', label: 'Videos' },
                ]}
                style={{ width: 180 }}
                clearable
              />
              <Select
                placeholder="Rating"
                value={ratingFilter}
                onChange={(v) => {
                  setRatingFilter(v || '');
                  setPage(1);
                }}
                data={[
                  { value: '', label: 'Cualquier rating' },
                  { value: '1', label: '1 estrella' },
                  { value: '2', label: '2 estrellas' },
                  { value: '3', label: '3 estrellas' },
                  { value: '4', label: '4 estrellas' },
                  { value: '5', label: '5 estrellas' },
                ]}
                style={{ width: 170 }}
                clearable
              />
            </Group>
          </Card>

          {/* Tabla */}
          <Card withBorder radius="lg" p="lg" style={cardStyle}>
            <Table.ScrollContainer minWidth={700}>
              <Table highlightOnHover verticalSpacing="sm" withRowBorders style={{ textAlign: 'left' }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Usuario</Table.Th>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Tipo</Table.Th>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Rating</Table.Th>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Comentario</Table.Th>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Fecha</Table.Th>
                    <Table.Th style={{ width: 40 }} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginated.map((q) => (
                    <Table.Tr
                      key={q.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelected(q)}
                    >
                      <Table.Td>
                        <Stack gap={2}>
                          <Text size="sm" fw={500} style={{ color: textPrimary }}>
                            {usuarioDisplay(q)}
                          </Text>
                          {q.usuarioEmail && (
                            <Text size="xs" style={{ color: textMuted }}>
                              {q.usuarioEmail}
                            </Text>
                          )}
                        </Stack>
                      </Table.Td>
                      <Table.Td>{tipoBadge(q.tipoObjetivo)}</Table.Td>
                      <Table.Td>{renderStars(q.rating)}</Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={2} style={{ maxWidth: 320, color: textPrimary }}>
                          {q.comentario || <span style={{ color: textMuted }}>—</span>}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" style={{ color: textMuted }}>
                          {formatDate(q.fechaCreacion)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap" justify="flex-end">
                          <Tooltip label="Ver detalle">
                            <ActionIcon variant="subtle" color="gray" onClick={() => setSelected(q)}>
                              <IconEye size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Eliminar reporte">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={(e) => { e.stopPropagation(); setABorrar(q); }}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {paginated.length === 0 && !loading && (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Stack align="center" py="xl" gap="xs">
                          <IconMessageReport size={32} color={textMuted} />
                          <Text size="sm" ta="center" style={{ color: textMuted }}>
                            {quejas.length === 0
                              ? 'Aún no hay reportes de usuarios'
                              : 'No se encontraron reportes con esos filtros'}
                          </Text>
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            {filtered.length > itemsPerPage && (
              <Group justify="center" mt="md">
                <Pagination
                  total={Math.ceil(filtered.length / itemsPerPage)}
                  value={page}
                  onChange={setPage}
                  color="gray"
                  size="sm"
                />
              </Group>
            )}
          </Card>
        </Stack>

        {/* Modal de detalle */}
        <Modal
          opened={!!selected}
          onClose={() => setSelected(null)}
          title={selected ? `Reporte #${selected.id}` : ''}
          size="lg"
          centered
        >
          {selected && (
            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Usuario</Text>
                <Stack gap={0} align="flex-end">
                  <Text fw={500}>{usuarioDisplay(selected)}</Text>
                  {selected.usuarioEmail && (
                    <Text size="xs" c="dimmed">{selected.usuarioEmail}</Text>
                  )}
                </Stack>
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Tipo</Text>
                {tipoBadge(selected.tipoObjetivo)}
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">ID de la pregunta</Text>
                <Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>
                  {selected.referenciaId}
                </Text>
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Rating</Text>
                {renderStars(selected.rating)}
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Fecha</Text>
                <Text fw={500}>{formatDate(selected.fechaCreacion)}</Text>
              </Group>
              {selected.preguntaTexto && (
                <>
                  <Divider />
                  <Stack gap={4}>
                    <Text size="sm" c="dimmed">
                      {selected.tipoObjetivo?.toLowerCase() === 'video'
                        ? 'Video reportado'
                        : selected.tipoObjetivo?.toLowerCase() === 'flashcard'
                        ? 'Flashcard reportada'
                        : 'Pregunta reportada'}
                    </Text>
                    <Card withBorder p="sm" radius="md">
                      <Text size="sm">{selected.preguntaTexto}</Text>
                    </Card>
                    {(() => {
                      const link = linkConfig(selected);
                      return link ? (
                        <Button
                          component="a"
                          href={link.url}
                          variant="light"
                          size="xs"
                          leftSection={<IconExternalLink size={14} />}
                          mt={4}
                          style={{ alignSelf: 'flex-start' }}
                        >
                          {link.label}
                        </Button>
                      ) : null;
                    })()}
                  </Stack>
                </>
              )}
              <Divider />
              <Stack gap={4}>
                <Text size="sm" c="dimmed">Comentario del usuario</Text>
                <Card withBorder p="sm" radius="md">
                  <Text size="sm">
                    {selected.comentario || <span style={{ color: textMuted }}>El usuario no dejó comentario.</span>}
                  </Text>
                </Card>
              </Stack>
            </Stack>
          )}
        </Modal>

        {/* Confirmación de borrado */}
        <Modal
          opened={!!aBorrar}
          onClose={() => !borrando && setABorrar(null)}
          title="Eliminar reporte"
          centered
          size="sm"
          overlayProps={{ backgroundOpacity: 0.4, blur: 4 }}
        >
          <Stack gap="md">
            <Text size="sm" style={{ color: textMuted }}>
              ¿Seguro que quieres eliminar este reporte
              {aBorrar?.comentario ? <> (“{aBorrar.comentario.slice(0, 40)}”)</> : null}? Esta acción no se puede deshacer.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" color="gray" onClick={() => setABorrar(null)} disabled={borrando}>
                Cancelar
              </Button>
              <Button color="red" leftSection={<IconTrash size={16} />} onClick={confirmarBorrado} loading={borrando}>
                Eliminar
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </PageTransition>
  );
};

export default QuejasSugerenciasPage;
